import React, { useEffect, useState } from "react";
import { Col, Form, Input, Modal, Row, Spin, message } from "antd";
import styled from "styled-components";
import { ButtonFilled } from "reusableComponent/UIButtons";

const StyledModal = styled(Modal)`
  .ant-form-item {
    margin-bottom: 12px;
  }
`;

const UNSAFE_PATTERN = /<script|<iframe|javascript:|onerror=|onload=|<[^>]*>|[{}\[\]\\%]/i;

const handleDecimalKeyDown = (e, maxDecimals = 2) => {
  const { key, target } = e;
  const { value, selectionStart, selectionEnd } = target;
  const allowedKeys = [
    "Backspace", "Tab", "ArrowLeft", "ArrowRight", 
    "Delete", "Home", "End", "Control", "Meta"
  ];
  if (allowedKeys.includes(key)) return;
  if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(key.toLowerCase())) {
    return;
  }
  if (!/[\d.]/.test(key)) {
    e.preventDefault();
    return;
  }
  if (key === "." && value.includes(".")) {
    e.preventDefault();
    return;
  }
  if (key === "." && value.length === 0) {
    e.preventDefault();
    return;
  }
  const dotIndex = value.indexOf(".");
  if (
    dotIndex !== -1 &&
    selectionStart > dotIndex &&
    value.substring(dotIndex + 1).length >= maxDecimals &&
    selectionStart === selectionEnd
  ) {
    e.preventDefault();
  }
};

// Validator for target chargeability (0-100, allows decimals)
const validateTargetChargeability = (_, value) => {
  if (!value) return Promise.resolve();
  if (UNSAFE_PATTERN.test(value)) {
    return Promise.reject("Invalid characters or HTML tags are not allowed.");
  }
  const num = parseFloat(value);
  if (isNaN(num)) {
    return Promise.reject("Please enter a valid number.");
  }
  const strValue = value.toString();
  if (strValue.includes('.')) {
    const decimals = strValue.split('.')[1];
    if (decimals && decimals.length > 2) {
      return Promise.reject("Maximum 2 decimal places allowed.");
    }
  }
  return Promise.resolve();
};

// Validator for decimal fields (standard and rate)
const validateDecimalValue = (fieldName) => (_, value) => {
  if (!value) return Promise.resolve();
  const num = parseFloat(value);
  if (isNaN(num)) {
    return Promise.reject(`Please enter a valid decimal value for ${fieldName}.`);
  }
  if (num < 0) {
    return Promise.reject(`${fieldName} must be a positive number.`);
  }
  if (num > 999999999) {
    return Promise.reject(`${fieldName} value is too large.`);
  }
  const strValue = value.toString();
  if (strValue.includes('.')) {
    const decimals = strValue.split('.')[1];
    if (decimals && decimals.length > 2) {
      return Promise.reject("Maximum 2 decimal places allowed.");
    }
  }
  return Promise.resolve();
};

function getRandomNumber(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const EditChargeOut = ({
  isEdit,
  setIsEdit,
  setRecordValue,
  loading,
  recordValue,
  handleFormSubmit,
  isLoading,
}) => {
  const [form] = Form.useForm();
  const [isView, setView] = useState();
  const unsafePattern = /<[^ ]*|[{}\[\]\/\\%!@#$^&*()]/i;


  useEffect(() => {
    if (isEdit) {
      form.resetFields();
      setRecordValue({});

      const num = getRandomNumber(1, 10000);
      setView(num);
      
      // Set form values from record
      if (recordValue && Object.keys(recordValue).length > 0) {
        form.setFieldsValue({
          jobgradecode: recordValue.jobgradecode,
          targetchargeability: recordValue.targetchargeability,
          standard: recordValue.standard,
          rate: recordValue.rate,
          jobgrade: recordValue.jobgrade,
        });
        setRecordValue(recordValue);
      }
    }
  }, [isEdit, recordValue]);

  const onFormSubmit = async (values) => {
    try {
      const { targetchargeability, rate } = values;
      if (targetchargeability) {
        const tcNum = parseFloat(targetchargeability);
        if (tcNum < 0 || tcNum > 100) {
          message.error("Target Chargeability must be between 0 and 100.");
          return;
        }
      }
      if (rate) {
        const rateNum = parseFloat(rate);
        if (rateNum <= 0) {
          message.error("Rate must be greater than 0.");
          return;
        }
      }
      await handleFormSubmit(values);
    } catch (error) {
      console.error("Form submission error:", error);
      message.error("Failed to save. Please check your inputs.");
    }
  };

  const handleModalCancel = () => {
    const isModified = form.isFieldsTouched();
    if (isModified) {
      Modal.confirm({
        title: "Discard changes?",
        content: "You have unsaved changes. Are you sure you want to close?",
        okText: "Yes, discard",
        cancelText: "No, keep editing",
        onOk: () => {
          setRecordValue({});
          form.resetFields();
          setIsEdit(false);
        },
      });
    } else {
      setRecordValue({});
      form.resetFields();
      setIsEdit(false);
    }
  };

  return (
    <Spin spinning={isLoading}>
      <StyledModal
        title="Edit Chargeout Rates"
        width={900}
        open={isEdit}
        footer={false}
        onCancel={handleModalCancel}
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={onFormSubmit}
          initialValues={
            Object.keys(recordValue).length ? recordValue : undefined
          }
          validateTrigger={["onChange", "onBlur"]}
        >
          <Row gutter={16}>
            {/* Job Grade Code - Read Only */}
            <Col span={12}>
              <Form.Item 
                name="jobgradecode" 
                label="Job Grade Code"
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item 
                name="jobgrade" 
                label="Job Grade Title"
              >
                <Input />
              </Form.Item>
            </Col>

            {/* Target Chargeability - Editable with strict validation */}
            <Col span={12}>
              <Form.Item
                name="targetchargeability"
                label="Target Chargeability (%)"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && unsafePattern.test(value)) {
                        return Promise.reject(
                          "Invalid characters or HTML tags are not allowed."
                        );
                      }
                      return Promise.resolve();
                    }
                  },
                  {
                    pattern: /^(100|[1-9]?[0-9])$/,
                    message: "Please enter a value between 0 and 100.",
                  }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="standard"
                label="Standard"
                rules={[
                  {
                    validator: validateTargetChargeability,
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="e.g., 85.50"
                  step={0.01}
                  onKeyDown={(e) => handleDecimalKeyDown(e, 2)}
                />
              </Form.Item>
            </Col>

            {/* Standard - Read Only */}
            <Col span={12}>
              <Form.Item
                name="standard"
                label="Standard Rate"
                tooltip="This field is read-only"
              >
                <Input disabled />
              </Form.Item>
            </Col>

            {/* Rate - Editable with strict validation */}
            <Col span={12}>
              <Form.Item
                name="rate"
                label={recordValue?.country || "Rate"}
                rules={[
                  {
                    required: true,
                    message: "Rate is required.",
                  },
                  {
                    validator: validateDecimalValue("Rate"),
                  },
                ]}
                tooltip="Enter the chargeout rate (positive decimal value)"
              >
                <Input
                  type="number"
                  placeholder="e.g., 150.00"
                  min={0}
                  step={0.01}
                  onKeyDown={(e) => handleDecimalKeyDown(e, 2)}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="w-100 text-end" style={{ marginTop: 16 }}>
            <ButtonFilled
              className="text-end"
              htmlType="submit"
              loading={loading}
              disabled={loading || isLoading}
            >
              Save
            </ButtonFilled>
          </div>
        </Form>

        {/* <GridBuilder
          formStructureJson={ChargeoutFormJson}
          initialValues={recordValue}
          formSubmit={handleFormSubmit}
          loading={loading}
          isModalOpen={true}
        /> */}
      </StyledModal>
    </Spin>
  );
};